import React, { useEffect, useState } from 'react'
import SurveyPreparedQuestions from '../SurveyTempleteFeature/SurveyPreparedQuestions'
import SurveyCreateQuestions from '../SurveyTempleteFeature/SurveyCreateQuestions'
import SurveyItemDropdown from '../SurveyTempleteFeature/components/SurveyItemDropdown'
import { Box, Card, CardContent, LinearProgress, Typography } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { SurveyItemQuestionType } from '../../models/SurveyTemplate'
import { grey } from '../../colors'
import { ADDED_QUESTION_TYPES } from '../../consts/addedQuestionTypes'
import { useUserProfile } from '../../hooks/useUserProfile'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { LookUpModal } from './LookUpModal'
import { getLookUpQuestion } from '../../APIManager/LookUpManager'
import { useQuery } from '@tanstack/react-query'
import { useAppDispatch } from '../../store'
import { getSurveySections } from '../../store/slices/SurveyTemplateSlice/selectors'
import { setSectionDescription, addSurveyQuestionInSection } from '../../store/slices/SurveyTemplateSlice'
import { type Question } from '../SurveyTempleteFeature/types'
import SectionDescriptionField from '../SurveyTempleteFeature/components/SectionDescriptionField'

interface AcademicUnitAndInstructorAddedQuestionsFeatureProps {
  /**
   * The Course Section type's question limit, from the shared set's own response
   * (`section_question_count`). Undefined until the backend ships it, or on the other two question
   * types, which take their limit from the `questionCount` search param instead.
   */
  sectionQuestionLimit?: number
}

export const AcademicUnitAndInstructorAddedQuestionsFeature: React.FC<AcademicUnitAndInstructorAddedQuestionsFeatureProps> = ({
  sectionQuestionLimit
}) => {
  const [isCountComplete, setIsCountComplete] = React.useState<boolean>(false)
  const [isLookUpModalOpen, setIsLookUpModalOpen] = useState(false)
  const dispatch = useAppDispatch()
  const surveySectionsList = getSurveySections()
  const section = surveySectionsList?.[0]
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const type = params.get('type')

  const questionCount = params.get('questionCount')
  const categoryName = params.get('categoryName')
  const isQuestionAllowed = params.get('isInstructorAddedQuestionsAllowed')
  const { data: userProfile } = useUserProfile()

  const isSectionSet = type === ADDED_QUESTION_TYPES.SECTION
  // Each question type counts towards its own cap.
  const capForThisSet = isSectionSet ? sectionQuestionLimit : questionCount
  // Only the section type can have an *unknown* cap: its limit rides on the shared set's own
  // response and is undefined until the backend ships `section_question_count`. The other two types
  // always read theirs from the search param, so their behaviour — including a 0 that blocks
  // selection — is left exactly as it was.
  const isCapUnknown = isSectionSet && sectionQuestionLimit == null

  const { addedQuestions, totalQuestions, progress } = React.useMemo(() => {
    const added = section?.surveyItems?.filter(
      (item) => item.itemType !== SurveyItemQuestionType.TITLE
    ).length ?? 0
    const total = Number(capForThisSet)
    const prog = total > 0 ? (added / total) * 100 : 0
    return { addedQuestions: added, totalQuestions: total, progress: prog }
  }, [section?.surveyItems, capForThisSet])

  useEffect(() => {
    if (progress === 100) {
      setIsCountComplete(true)
    } else if (progress !== 100 && progress > 0) {
      setIsCountComplete(false)
    }
  }, [progress])

  const instructorName = userProfile?.user?.name
  const isSectionType = isSectionSet
  const title = isSectionType
    // No instructor name: this set belongs to the section, not to whoever opened it.
    ? 'Course Section Questions'
    : type === ADDED_QUESTION_TYPES.INSTRUCTOR
      ? `Instructor Added Questions${instructorName != null ? ` by ${instructorName}` : ''}`
      : `${categoryName} Added Questions`

  // The bar needs a real cap for this set. The instructor type always has one; the section type's
  // only arrives once the backend puts `section_added_questions` on the dashboard payload — until
  // then the shared set shows a plain tally rather than a bar counting towards the *other* type's
  // limit (which is 0 when per-instructor questions are off). Server-side enforcement is
  // unaffected either way.
  const showQuestionLimit = Number(capForThisSet) > 0
  const showQuestionTally = !showQuestionLimit && isSectionType && addedQuestions > 0

  // logic for checking for school-approved questions
  const { data: schoolApprovedQuestionIds = [], isLoading: loading } = useQuery<string[]>({
    queryKey: ['schoolApprovedQuestions'],
    queryFn: async () => {
      const res = await getLookUpQuestion({ school_approved: true })
      return res.data.map((question: Question) => question.itemId) || []
    },
    staleTime: 1000,
    retry: false
  })

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          width: 800,
          flexDirection: 'column',
          mx: 'auto',
          mt: 6
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display='flex' alignItems="center" justifyContent="space-between" >
            <Typography variant="subtitle1" fontWeight={500} fontSize={16} color={grey[250]}>
              {title}
            </Typography>
            {showQuestionTally &&
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {`${addedQuestions} ${addedQuestions === 1 ? 'question' : 'questions'} added`}
              </Typography>
            }
            {showQuestionLimit &&
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    width: 60,
                    height: 8,
                    borderRadius: 5,
                    bgcolor: '#F3F4F6'
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {`${addedQuestions} out of ${totalQuestions} questions added`}
                </Typography>
              </Box>
            }
          </Box>

          <Typography>Section description (Optional)</Typography>
          <SectionDescriptionField
            sx={{ marginY: 2.5 }}
            value={section?.descr ?? ''}
            onChange={(value) => {
              dispatch(setSectionDescription({ surveySectionId: section?.surveySectionId, value }))
            }}
          />
          <SurveyPreparedQuestions
            isAdminAddedQuestion={true}
            surveyItems={section.surveyItems}
            sectionIndex={0}
            section={section}
            isCountComplete={isCountComplete}
            arrayOfSchoolApprovedQuestionIds={schoolApprovedQuestionIds}
          />
          <SurveyCreateQuestions isAdminAddedQuestion={true} sectionIndex={0} section={section} isCountComplete={isCountComplete} />
          <Box display='flex' justifyContent='center' alignItems='center' gap={2}>
            <SurveyItemDropdown
              sectionNumber={0}
              isCountComplete={isCountComplete}
              isQuestionAllowed={
                // The param carries the *instructor* type's `isCustomQuestionAllowed`, so it says
                // nothing about the shared set — and reads 'false' on a section-only project, which
                // would deny custom questions exactly where the section type may well allow them.
                // The section type's own flag is not on `InstructorDistribution` yet, so rather than
                // apply the wrong type's policy the shared set asserts no restriction until it is.
                isSectionSet ? false : isQuestionAllowed !== 'true'
              }
            />
            <Box mt={6}>
              <GenericButton
                buttonSize={ButtonSize.Medium}
                buttonType={ButtonType.Secondary}
                label='Lookup Question'
                startIcon={<SearchOutlinedIcon />}
                style={{ fontSize: '16px', fontWeight: 600, color: grey[950] }}
                onClick={() => { setIsLookUpModalOpen(true) }}
                disabled={!section?.surveySectionId}
              />
            </Box>
            {isLookUpModalOpen && (
              <LookUpModal
                lookUpData={{ selectedIds: section?.surveyItems?.map(item => item)?.filter(id => id != null) ?? [] }}
                onClose={() => { setIsLookUpModalOpen(false) }}
                // This set's own cap, not the instructor type's: on a Course Section-only project
                // `questionCount` is 0, which blocked every selection in here.
                totalQuestionLimit={isCapUnknown ? undefined : Number(capForThisSet)}
                // With no cap to enforce yet, lookup leans on the server's own enforcement rather
                // than blocking outright — same call the tally-instead-of-bar display makes above.
                ignoreLimit={isCapUnknown}
                currentQuestionCount={addedQuestions}
                onAdd={({ removedQuestions, selectedQuestions }) => {
                  if (section?.surveySectionId) {
                    selectedQuestions.forEach(question => {
                      dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question, removedQuestions }))
                    })
                  }
                  setIsLookUpModalOpen(false)
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </>
  )
}
